/**
 * Apply shared/scoped saved views from the URL and index source decisions.
 */
import { leadFactorySavedViewScopes } from "./dashboard-urls.js";
import {
  savedViewUrlParam,
  setLeadFactoryMetricsDays,
  setSavedViewFilters,
  setSavedViewUrl
} from "./dashboard-saved-view-state.js";
import { fetchSavedViewById } from "./dashboard-lead-factory-fetch.js";
import type { LeadFactorySavedView, SourceDecisionRecord } from "./dashboard-lead-factory-types.js";

export function latestSourceDecisionIndex(decisions: SourceDecisionRecord[]): Map<string, SourceDecisionRecord> {
  const indexed = new Map<string, SourceDecisionRecord>();
  for (const decision of decisions) {
    const key = sourceDecisionKey(decision.county, decision.target_month, decision.blocker_code, decision.decision);
    if (!indexed.has(key)) indexed.set(key, decision);
  }
  return indexed;
}

export function sourceDecisionKey(county: string, targetMonth: string, blockerCode: string, decision: string): string {
  return [county, targetMonth, blockerCode, decision].map((part) => part.trim().toLowerCase()).join("|");
}

export async function applyUrlSavedViews(): Promise<void> {
  const params = new URLSearchParams(window.location.search);
  const viewIds = new Set<string>();
  const sharedViewId = params.get("lf_view")?.trim();
  if (sharedViewId) viewIds.add(sharedViewId);
  for (const scope of leadFactorySavedViewScopes) {
    const scopedViewId = params.get(savedViewUrlParam(scope))?.trim();
    if (scopedViewId) viewIds.add(scopedViewId);
  }
  if (viewIds.size === 0) return;
  const savedViews = await Promise.all(Array.from(viewIds).map((viewId) => fetchSavedViewById(viewId)));
  savedViews.forEach(({ view }) => applySavedView(view));
}

export function applySavedView(view: LeadFactorySavedView): void {
  if (view.scope === "business_metrics") {
    const days = Number(view.filters.days);
    if (Number.isFinite(days)) setLeadFactoryMetricsDays(days);
    setSavedViewUrl(view.scope, view.id);
    return;
  }
  setSavedViewFilters(view.scope, view.filters);
  setSavedViewUrl(view.scope, view.id);
}
